import axios from 'axios';
import type { AxiosInstance, AxiosError } from 'axios';
import type {
  ApiResponse,
  SignupRequest,
  LoginRequest,
  AuthResponse,
  CreateTeamRequest,
  UpdateTeamRequest,
  Team,
  UpdatePlayerTargetRequest,
  BulkUpdateTargetsRequest,
  BulkImportByNameRequest,
  BulkImportResult
} from '@shared/types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Request interceptor to add auth token (ID token for API Gateway Cognito Authorizer)
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('idToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle errors
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const requestUrl = error.config?.url || '';
        const isAuthEndpoint = requestUrl.startsWith('/auth/');

        if (error.response?.status === 401 && !isAuthEndpoint) {
          // Token expired, clear session (skip for auth endpoints — they handle 401s themselves)
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          window.location.href = '/login';
        } else if (error.response?.status === 403) {
          // Forbidden - user lacks permission for this action
          window.location.href = '/dashboard';
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async signup(data: SignupRequest): Promise<any> {
    const response = await this.client.post<ApiResponse<any>>('/auth/signup', data);
    return response.data.data;
  }

  async login(data: LoginRequest): Promise<AuthResponse> {
    const response = await this.client.post<ApiResponse<AuthResponse>>('/auth/login', data);
    return response.data.data!;
  }

  async verify(data: { email: string; code: string }): Promise<any> {
    const response = await this.client.post<ApiResponse<any>>('/auth/verify', data);
    return response.data.data;
  }

  async resendCode(data: { email: string }): Promise<any> {
    const response = await this.client.post<ApiResponse<any>>('/auth/resend-code', data);
    return response.data.data;
  }

  async forgotPassword(data: { email: string }): Promise<any> {
    const response = await this.client.post<ApiResponse<any>>('/auth/forgot-password', data);
    return response.data.data;
  }

  async resetPassword(data: { email: string; code: string; newPassword: string }): Promise<any> {
    const response = await this.client.post<ApiResponse<any>>('/auth/reset-password', data);
    return response.data.data;
  }

  // Team endpoints
  async getTeams(): Promise<Team[]> {
    const response = await this.client.get<ApiResponse<{ teams: Team[] }>>('/teams');
    return response.data.data!.teams;
  }

  async getTeam(teamId: string): Promise<Team> {
    const response = await this.client.get<ApiResponse<{ team: Team }>>(`/teams/${teamId}`);
    return response.data.data!.team;
  }

  async createTeam(data: CreateTeamRequest): Promise<Team> {
    const response = await this.client.post<ApiResponse<{ team: Team }>>('/teams', data);
    return response.data.data!.team;
  }

  async updateTeam(teamId: string, data: UpdateTeamRequest): Promise<Team> {
    const response = await this.client.put<ApiResponse<{ team: Team }>>(`/teams/${teamId}`, data);
    return response.data.data!.team;
  }

  async deleteTeam(teamId: string): Promise<void> {
    await this.client.delete(`/teams/${teamId}`);
  }

  // Player endpoints
  async searchPlayers(query: string): Promise<any[]> {
    const response = await this.client.get<ApiResponse<{ players: any[] }>>('/players/search', {
      params: { q: query }
    });
    return response.data.data!.players;
  }

  async getPlayerTargets(): Promise<Record<number, { target: number; playerName?: string; team?: string }>> {
    const response = await this.client.get<ApiResponse<{ targets: Record<number, { target: number; playerName?: string; team?: string }> }>>('/players/targets');
    return response.data.data!.targets;
  }

  async updatePlayerTarget(playerId: number, data: UpdatePlayerTargetRequest): Promise<any> {
    const response = await this.client.put(`/players/targets/${playerId}`, data);
    return response.data.data;
  }

  async deletePlayerTarget(playerId: number): Promise<any> {
    const response = await this.client.delete(`/players/targets/${playerId}`);
    return response.data.data;
  }

  async bulkUpdatePlayerTargets(data: BulkUpdateTargetsRequest): Promise<any> {
    const response = await this.client.post('/players/targets/bulk', data);
    return response.data.data;
  }

  async bulkImportByName(data: BulkImportByNameRequest): Promise<{ result: BulkImportResult; message: string }> {
    const response = await this.client.post<ApiResponse<{ result: BulkImportResult; message: string }>>('/players/targets/bulk-import', data);
    return response.data.data!;
  }

  // Custom players endpoints
  async getCustomPlayers(): Promise<any[]> {
    const response = await this.client.get<ApiResponse<{ players: any[] }>>('/players/custom-players');
    return response.data.data!.players;
  }

  async addCustomPlayer(data: { playerName: string; position: string; team: string }): Promise<any> {
    const response = await this.client.post<ApiResponse<any>>('/players/custom-players', data);
    return response.data.data;
  }

  async deleteCustomPlayer(playerId: number): Promise<any> {
    const response = await this.client.delete<ApiResponse<any>>('/players/custom-players', {
      params: { playerId }
    });
    return response.data.data;
  }

  // Config endpoints
  async getConfig(): Promise<Record<string, any>> {
    const response = await this.client.get<ApiResponse<{ config: Record<string, any> }>>('/config');
    return response.data.data!.config;
  }

  async updateConfig(data: Record<string, any>): Promise<any> {
    const response = await this.client.put('/config', data);
    return response.data.data;
  }
}

export const api = new ApiClient();
